import type { FC, FormEvent, ReactNode } from 'react'

import Link from 'next/link'
import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { ArrowRight, LoaderCircle, MailCheck } from 'lucide-react'
import { sendJson } from '@/lib/request'
import { Seo } from '@/components/generals/seo'
import { Button } from '@/components/generals/button'
import { AuthShell } from '@/components/portal/auth-shell'
import { PasswordInput } from '@/components/portal/password-input'
import { Field, FormMessage, TextInput } from '@/components/generals/form-field'
import { MIN_PASSWORD_LENGTH } from '@/constants/portal'

const RESEND_SECONDS = 60

/*
   Set up or reset access
   Step 1: the resident enters the email the estate office has on record.
   Step 2: they enter the six-digit code we email them and choose a password,
   then go straight into the portal.
*/
const Access: FC = (): ReactNode => {
    const router = useRouter()
    const [step, setStep] = useState<'email' | 'code'>('email')
    const [email, setEmail] = useState('')
    const [code, setCode] = useState('')
    const [password, setPassword] = useState('')
    const [confirmation, setConfirmation] = useState('')
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState('')
    const [notice, setNotice] = useState('')
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
    const [cooldown, setCooldown] = useState(0)

    // Counts down the wait before another code can be requested
    useEffect(() => {
        if (cooldown <= 0) return

        const timer = window.setTimeout(() => setCooldown(current => current - 1), 1000)

        return () => window.clearTimeout(timer)
    }, [cooldown])

    const requestCode = async () => {
        setBusy(true)
        setError('')

        const result = await sendJson('/api/residents/request-code', 'POST', { email })

        setBusy(false)

        if (!result.ok) {
            setError(result.data.message ?? "We could not send a code just now. Please try again.")
            return false
        }

        setNotice(result.data.message ?? '')
        setCooldown(RESEND_SECONDS)

        return true
    }

    const onEmailSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
            setError("Please enter a valid email address.")
            return
        }

        if (await requestCode()) setStep('code')
    }

    const onCodeSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        const errors: Record<string, string> = {}

        if (code.replace(/\D/g, '').length !== 6) errors.code = "Enter all six digits from the email."
        if (password.length < MIN_PASSWORD_LENGTH) errors.password = `Use at least ${MIN_PASSWORD_LENGTH} characters.`
        if (confirmation !== password) errors.confirmation = "The passwords don't match."

        setFieldErrors(errors)

        if (Object.keys(errors).length > 0) {
            setError('')
            return
        }

        setBusy(true)
        setError('')

        const result = await sendJson<{ redirect?: string }>('/api/residents/set-password', 'POST', { email, code, password })

        if (result.ok) {
            await router.replace(result.data.redirect ?? '/residents/dashboard')
            return
        }

        setBusy(false)
        setFieldErrors(result.data.errors ?? {})
        setError(result.data.message ?? "We could not save your password. Please try again.")
    }

    return (
        <AuthShell
            eyebrow='Resident access'
            title={step === 'email' ? 'Set up or reset your access' : 'Check your email'}
            intro={step === 'email'
                ? "Enter the email address the estate office has on record for you. We'll send a one-time code so you can choose your password."
                : 'Enter the six-digit code we sent you and choose a password. The code expires in 15 minutes.'}
        >
            <Seo title='Set up resident access' description='Set up or reset your Rockwood Estate resident portal access.' noIndex />

            {step === 'email' && (
                <form noValidate onSubmit={onEmailSubmit} className='space-y-5'>
                    <Field id='email' label='Email address'>
                        <TextInput
                            id='email'
                            name='email'
                            type='email'
                            autoComplete='email'
                            value={email}
                            onChange={event => setEmail(event.target.value)}
                            required
                        />
                    </Field>

                    {error && (
                        <FormMessage tone='error'>
                            {error}
                        </FormMessage>
                    )}

                    <Button type='submit' size='lg' disabled={busy} className='w-full'>
                        {busy
                            ? <LoaderCircle aria-hidden='true' className='size-4 animate-spin' />
                            : null}

                        {busy ? 'Sending code' : 'Email me a code'}

                        {!busy && <ArrowRight aria-hidden='true' className='size-4 transition-transform duration-300 group-hover/button:translate-x-0.5' />}
                    </Button>

                    <p className='text-center text-sm text-muted-foreground'>
                        Already have a password?{' '}
                        <Link href='/residents' className='link-underline pb-0.5 font-medium text-forest'>
                            Sign in
                        </Link>
                    </p>
                </form>
            )}

            {step === 'code' && (
                <form noValidate onSubmit={onCodeSubmit} className='space-y-5'>
                    {notice && (
                        <FormMessage tone='success' className='flex gap-3'>
                            <MailCheck aria-hidden='true' className='mt-0.5 size-4 shrink-0' />
                            <span>
                                {notice}
                            </span>
                        </FormMessage>
                    )}

                    <Field id='code' label='Six-digit code' error={fieldErrors.code}>
                        <TextInput
                            id='code'
                            name='code'
                            inputMode='numeric'
                            autoComplete='one-time-code'
                            maxLength={6}
                            value={code}
                            onChange={event => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                            aria-invalid={Boolean(fieldErrors.code)}
                            aria-describedby={fieldErrors.code ? 'code-error' : undefined}
                            className='font-heading text-2xl tracking-[0.5em]'
                            required
                        />
                    </Field>

                    <Field id='new-password' label='New password' hint={`At least ${MIN_PASSWORD_LENGTH} characters. A short phrase is easy to remember.`} error={fieldErrors.password}>
                        <PasswordInput
                            id='new-password'
                            name='new-password'
                            autoComplete='new-password'
                            value={password}
                            onChange={event => setPassword(event.target.value)}
                            aria-invalid={Boolean(fieldErrors.password)}
                            aria-describedby={fieldErrors.password ? 'new-password-error' : 'new-password-hint'}
                            required
                        />
                    </Field>

                    <Field id='confirm-password' label='Confirm password' error={fieldErrors.confirmation}>
                        <PasswordInput
                            id='confirm-password'
                            name='confirm-password'
                            autoComplete='new-password'
                            value={confirmation}
                            onChange={event => setConfirmation(event.target.value)}
                            aria-invalid={Boolean(fieldErrors.confirmation)}
                            aria-describedby={fieldErrors.confirmation ? 'confirm-password-error' : undefined}
                            required
                        />
                    </Field>

                    {error && (
                        <FormMessage tone='error'>
                            {error}
                        </FormMessage>
                    )}

                    <Button type='submit' size='lg' disabled={busy} className='w-full'>
                        {busy
                            ? <LoaderCircle aria-hidden='true' className='size-4 animate-spin' />
                            : null}

                        {busy ? 'Saving' : 'Save password and sign in'}
                    </Button>

                    <div className='flex flex-wrap items-center justify-between gap-3 text-sm'>
                        <button
                            type='button'
                            onClick={() => requestCode()}
                            disabled={busy || cooldown > 0}
                            className='link-underline pb-0.5 font-medium text-forest disabled:cursor-not-allowed disabled:text-muted-foreground'
                        >
                            {cooldown > 0 ? `Send a new code in ${cooldown}s` : 'Send a new code'}
                        </button>

                        <button
                            type='button'
                            onClick={() => {
                                setStep('email')
                                setCode('')
                                setError('')
                                setNotice('')
                                setFieldErrors({})
                            }}
                            className='link-underline pb-0.5 text-muted-foreground hover:text-charcoal'
                        >
                            Use a different email
                        </button>
                    </div>

                    <p className='text-xs leading-relaxed text-muted-foreground'>
                        No email after a few minutes? Check your spam folder, or ask the estate office to confirm the address they have for you.
                    </p>
                </form>
            )}
        </AuthShell>
    )
}

export default Access
